import { useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { DollarSign, TrendingUp, Users, Crown, RefreshCw, ChevronLeft, ChevronRight, ArrowUpRight, ArrowDownRight, RotateCcw, CreditCard, BarChart3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('admin_token');
const api = (method, url, data) => axios({ method, url: `${API_URL}/api${url}`, data, headers: { Authorization: `Bearer ${getToken()}` } });
const PLAN_COLORS = { Free: '#78716C', Silver: '#A8A29E', Gold: '#F2A900', Platinum: '#D9381E', free: '#78716C', silver: '#A8A29E', gold: '#F2A900', platinum: '#D9381E' };

function formatCurrency(v) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v);
}

export default function AdminRevenuePage() {
  const [revenue, setRevenue] = useState(null);
  const [payments, setPayments] = useState([]);
  const [paymentTotal, setPaymentTotal] = useState(0);
  const [paymentPage, setPaymentPage] = useState(1);
  const [paymentPages, setPaymentPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [refundDialog, setRefundDialog] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [planFilter, setPlanFilter] = useState('all');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [revRes, payRes] = await Promise.all([
        api('get', '/admin/revenue'),
        api('get', `/admin/payments?plan=${planFilter}&page=${paymentPage}&limit=15`),
      ]);
      setRevenue(revRes.data);
      setPayments(payRes.data.payments || []);
      setPaymentTotal(payRes.data.total || 0);
      setPaymentPages(payRes.data.pages || 1);
    } catch (e) {
      toast.error('Failed to load revenue data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [planFilter, paymentPage]);

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      const res = await api('post', '/subscriptions/sync-all');
      toast.success(`Synced: ${res.data.activated} activated out of ${res.data.total_pending} pending`);
      fetchData();
    } catch (e) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleRefund = async () => {
    if (!refundDialog || !refundAmount) return;
    try {
      await api('post', `/admin/refunds/${refundDialog.id}`, { amount: parseFloat(refundAmount), reason: refundReason });
      toast.success('Refund processed');
      setRefundDialog(null);
      setRefundAmount('');
      setRefundReason('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refund failed');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-3 border-[#D9381E] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!revenue) return <p className="text-[#78716C]">Failed to load data</p>;

  const planPieData = Object.entries(revenue.plan_counts || {}).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1), value
  }));
  const revenuePieData = Object.entries(revenue.plan_revenue || {}).map(([name, value]) => ({ name, value }));

  const metricCards = [
    { icon: DollarSign, label: 'Total Revenue', value: formatCurrency(revenue.total_revenue), sub: `${revenue.total_payments} transactions`, color: 'text-green-600', bg: 'bg-green-100' },
    { icon: TrendingUp, label: 'Monthly Revenue (MRR)', value: formatCurrency(revenue.mrr), sub: `ARR: ${formatCurrency(revenue.arr)}`, color: 'text-[#D9381E]', bg: 'bg-[#D9381E]/10' },
    { icon: Users, label: 'ARPU', value: formatCurrency(revenue.arpu), sub: `${revenue.total_paid_users} paid users`, color: 'text-[#F2A900]', bg: 'bg-[#F2A900]/10' },
    { icon: Crown, label: 'Conversion Rate', value: `${revenue.conversion_rate}%`, sub: `${revenue.total_paid_users} / ${revenue.total_users} users`, color: 'text-purple-600', bg: 'bg-purple-100' },
    { icon: RotateCcw, label: 'Total Refunds', value: formatCurrency(revenue.total_refunds), sub: `${revenue.refund_count} refunds`, color: 'text-red-600', bg: 'bg-red-100' },
    { icon: CreditCard, label: 'Pending Orders', value: revenue.pending_subscriptions, sub: 'Awaiting payment', color: 'text-blue-600', bg: 'bg-blue-100' },
  ];

  return (
    <div data-testid="admin-revenue-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#2D2424]">Revenue & Subscriptions</h1>
          <p className="text-sm text-[#78716C]">Manage payments, subscriptions and revenue analytics</p>
        </div>
        <Button onClick={handleSyncAll} disabled={syncing} variant="outline"
          className="rounded-lg gap-2 border-[#E7E5E4]" data-testid="sync-shopify-btn">
          <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Syncing...' : 'Sync Shopify'}
        </Button>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {metricCards.map((m, i) => (
          <Card key={i} className="p-4 rounded-xl border border-[#E7E5E4] bg-white" data-testid={`revenue-metric-${i}`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg ${m.bg} flex items-center justify-center`}>
                <m.icon className={`w-5 h-5 ${m.color}`} />
              </div>
              <div>
                <div className="text-xl font-bold text-[#2D2424]">{m.value}</div>
                <div className="text-xs text-[#78716C]">{m.label}</div>
                <div className="text-[10px] text-[#78716C]">{m.sub}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <Card className="lg:col-span-2 p-5 rounded-xl border border-[#E7E5E4] bg-white">
          <h3 className="text-sm font-semibold text-[#2D2424] mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-[#D9381E]" /> Daily Revenue (Last 30 Days)
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={revenue.daily_revenue || []}>
              <defs>
                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#D9381E" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#D9381E" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${v}`} />
              <Tooltip formatter={(v) => formatCurrency(v)} />
              <Area type="monotone" dataKey="revenue" stroke="#D9381E" fill="url(#revGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5 rounded-xl border border-[#E7E5E4] bg-white">
          <h3 className="text-sm font-semibold text-[#2D2424] mb-4 flex items-center gap-2">
            <Crown className="w-4 h-4 text-[#F2A900]" /> Plan Distribution
          </h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={planPieData} cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={3} dataKey="value"
                label={({ name, value }) => value > 0 ? `${name}: ${value}` : ''}>
                {planPieData.map((entry, idx) => <Cell key={idx} fill={PLAN_COLORS[entry.name] || '#78716C'} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap justify-center gap-3 mt-2">
            {planPieData.map((e, i) => (
              <div key={i} className="flex items-center gap-1.5 text-xs">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: PLAN_COLORS[e.name] || '#78716C' }} />
                {e.name}: {e.value}
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Revenue by Plan */}
      {revenuePieData.length > 0 && (
        <Card className="p-5 rounded-xl border border-[#E7E5E4] bg-white mb-8">
          <h3 className="text-sm font-semibold text-[#2D2424] mb-4">Revenue by Plan</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {revenuePieData.map((entry, i) => (
              <div key={i} className="text-center p-3 rounded-lg bg-[#F9F5F0]">
                <div className="text-lg font-bold" style={{ color: PLAN_COLORS[entry.name] || '#2D2424' }}>{formatCurrency(entry.value)}</div>
                <div className="text-xs text-[#78716C]">{entry.name}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Payment History */}
      <Card className="rounded-xl border border-[#E7E5E4] bg-white overflow-hidden">
        <div className="p-4 border-b border-[#E7E5E4] flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[#2D2424]">Payment History ({paymentTotal})</h3>
          <Select value={planFilter} onValueChange={(v) => { setPlanFilter(v); setPaymentPage(1); }}>
            <SelectTrigger className="w-32 h-9"><SelectValue placeholder="All Plans" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Plans</SelectItem>
              <SelectItem value="silver">Silver</SelectItem>
              <SelectItem value="gold">Gold</SelectItem>
              <SelectItem value="platinum">Platinum</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F9F5F0] border-b border-[#E7E5E4]">
                <th className="text-left p-3 font-semibold">User</th>
                <th className="text-left p-3 font-semibold">Plan</th>
                <th className="text-left p-3 font-semibold">Amount</th>
                <th className="text-left p-3 font-semibold">Date</th>
                <th className="text-left p-3 font-semibold">Status</th>
                <th className="text-left p-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {payments.map(p => (
                <tr key={p.id} className="border-b border-[#E7E5E4]/50 hover:bg-[#F9F5F0]/50">
                  <td className="p-3 font-medium">{p.user_name || p.user_id?.slice(0, 8)}</td>
                  <td className="p-3">
                    <Badge style={{ background: `${PLAN_COLORS[p.plan_name?.toLowerCase()] || '#78716C'}20`, color: PLAN_COLORS[p.plan_name?.toLowerCase()] || '#78716C' }}>
                      {p.plan_name}
                    </Badge>
                  </td>
                  <td className="p-3 font-medium">{formatCurrency(p.amount)}</td>
                  <td className="p-3 text-[#78716C]">{new Date(p.created_at).toLocaleDateString()}</td>
                  <td className="p-3">
                    {p.refunded ? (
                      <Badge className="bg-red-100 text-red-700">Refunded (₹{p.refund_amount})</Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-700">Completed</Badge>
                    )}
                  </td>
                  <td className="p-3">
                    {!p.refunded && (
                      <Button size="sm" variant="outline" onClick={() => { setRefundDialog(p); setRefundAmount(String(p.amount)); }}
                        className="rounded-lg text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid={`refund-${p.id}`}>
                        <RotateCcw className="w-3 h-3 mr-1" /> Refund
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {payments.length === 0 && (
                <tr><td colSpan={6} className="p-12 text-center text-[#78716C]">No payments yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {paymentPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-[#E7E5E4]">
            <span className="text-xs text-[#78716C]">Page {paymentPage} of {paymentPages}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setPaymentPage(p => p - 1)} disabled={paymentPage <= 1}><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="outline" size="sm" onClick={() => setPaymentPage(p => p + 1)} disabled={paymentPage >= paymentPages}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </Card>

      {/* Refund Dialog */}
      <Dialog open={!!refundDialog} onOpenChange={() => { setRefundDialog(null); setRefundAmount(''); setRefundReason(''); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Process Refund</DialogTitle></DialogHeader>
          {refundDialog && (
            <div className="space-y-3">
              <div className="p-3 bg-[#F9F5F0] rounded-lg text-sm">
                <p><span className="font-medium">User:</span> {refundDialog.user_name}</p>
                <p><span className="font-medium">Plan:</span> {refundDialog.plan_name}</p>
                <p><span className="font-medium">Amount:</span> {formatCurrency(refundDialog.amount)}</p>
              </div>
              <div>
                <label className="text-sm font-medium">Refund Amount (₹)</label>
                <Input type="number" value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)}
                  max={refundDialog.amount} className="mt-1" data-testid="refund-amount-input" />
              </div>
              <div>
                <label className="text-sm font-medium">Reason</label>
                <Textarea value={refundReason} onChange={(e) => setRefundReason(e.target.value)}
                  placeholder="Refund reason..." rows={2} className="mt-1" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setRefundDialog(null)}>Cancel</Button>
                <Button onClick={handleRefund} className="bg-red-600 text-white" data-testid="confirm-refund-btn">
                  Process Refund
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
